import { Dispatch } from 'redux';
import { NavigateFunction } from 'react-router-dom';
import { saveAs } from 'file-saver';

import * as api from '../utils/api';

import { notifyError, notifySuccess } from '../utils/functions';
import { showOverlay } from './overlay';

interface CoursesState {
  list?: ICourse[];
  current?: ICourse;
}

const actions = {
  CLEAR_COURSE: 'lms-ui/courses/CLEAR_COURSE',
  UPDATE: 'lms-ui/courses/UPDATE',
  GET_COURSES: 'lms-ui/courses/GET_COURSES',
  GET_COURSE: 'lms-ui/courses/GET_COURSE',
};

interface CoursesAction {
  type: typeof actions.GET_COURSES | typeof actions.GET_COURSE | typeof actions.UPDATE;
  payload: ICourse | ICourse[];
}

type CoursesActionTypes = CoursesAction;

const initialState: CoursesState = {
  list: [],
  current: undefined,
};

export const update = (payload: CoursesState) => {
  return {
    type: actions.UPDATE,
    payload,
  };
};

export const clearCourse = () => {
  return {
    type: actions.CLEAR_COURSE,
  };
};

export const getCourseReport =
  ({ courseId, userId }: { courseId: string; userId: string }) =>
  async (dispatch: Dispatch) => {
    try {
      const response = await api.getCourseReport(courseId, userId);
      const blob = new Blob([response.data], { type: 'octet/stream' });
      saveAs(blob, `User Report.xlsx`);
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const getCourses = () => async (dispatch: Dispatch) => {
  try {
    const response = await api.getCourses();
    const list = response.data;
    dispatch({ type: actions.GET_COURSES, payload: list });
  } catch (e) {
    notifyError(e, dispatch);
  }
};

export const getCourse =
  ({ courseId }: { courseId: string }) =>
  async (dispatch: Dispatch) => {
    try {
      const response = await api.getCourse(courseId);
      const current = response.data as ICourse;
      dispatch({ type: actions.GET_COURSE, payload: current });
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

const getFormData = (course: ICourse) => {
  const formData = new FormData();
  const props = [
    'title',
    'subtitle',
    'description',
    'published',
    'logo',
    'expirationSpan',
    'expirationDate',
    'modulesInOrder',
    'certUponComplete',
    'blocks',
    'hideScoreBlock',
    'modules',
    'categories',
    'duration',
    'cleCredits',
    'authoredBy',
    'collaboration',
  ];

  props.forEach((p) => {
    const value = (course as IKey)[p];
    if (value) {
      switch (p) {
        case 'collaboration':
        case 'logo':
          formData.append(p, value);
          break;
        case 'blocks':
          value?.forEach((v: IBlock, i: number) => {
            Object.keys(v).forEach((k) => {
              formData.append(`${p}[${i}][${k}]`, v[k]);
            });
          });
          break;
        case 'modules':
          value?.forEach((v: IModule, i: number) => {
            Object.keys(v)
              .filter((k) => ['_id', 'categoryId'].includes(k))
              .forEach((k) => {
                if (v[k]) {
                  formData.append(`${p}[${i}][${k}]`, v[k]);
                }
              });
          });
          break;
        case 'categories':
          value?.forEach((v: ICategory, i: number) => {
            Object.keys(v).forEach((k) => {
              formData.append(`${p}[${i}][${k}]`, v[k]);
            });
          });
          break;
        case 'cleCredits':
          value?.forEach((v: ICLECredit, i: number) => {
            Object.keys(v).forEach((k) => {
              formData.append(`${p}[${i}][${k}]`, v[k]);
            });
          });
          break;
        default:
          formData.append(p, value.toString());
          break;
      }
    }
  });

  return formData;
};

export const addCourse =
  (course: ICourse, navigate: NavigateFunction) => async (dispatch: Dispatch) => {
    try {
      const formData = getFormData(course);
      const response = await api.addCourse(formData);
      const current = response.data as ICourse;
      dispatch(update({ current }));
      navigate(`/admin/courses/${current._id}`);
      notifySuccess('Course added');
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const updateCourse = (course: ICourse) => async (dispatch: Dispatch) => {
  try {
    const formData = getFormData(course);
    const response = await api.updateCourse(course._id as string, formData);
    const current = response.data as ICourse;
    dispatch(update({ current }));
    notifySuccess('Course updated');
  } catch (e) {
    notifyError(e, dispatch);
  }
};

export const deleteCourse =
  ({ courseId, navigate }: { courseId: string; navigate: NavigateFunction }) =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(showOverlay(true));
      await api.deleteCourse({ courseId });
      navigate(`/admin/courses`);
      dispatch(update({ current: undefined }));
      dispatch(showOverlay());
      notifySuccess('Course deleted');
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export default function reducer(state = initialState, action: CoursesActionTypes): CoursesState {
  switch (action.type) {
    case actions.UPDATE: {
      return {
        ...state,
        ...action.payload,
      };
    }
    case actions.GET_COURSES: {
      return {
        ...state,
        list: action.payload as ICourse[],
      };
    }
    case actions.GET_COURSE: {
      return {
        ...state,
        current: action.payload as ICourse,
      };
    }
    case actions.CLEAR_COURSE: {
      return {
        ...state,
        current: undefined,
      };
    }
    default:
      return state;
  }
}
